<template>
    <div v-if="summary" class="summary-data">
        <div v-if="isCompetence && competenceRows.length" class="competence-card">
            <div v-for="(row, i) in competenceRows" :key="i" class="competence-row">
                <span class="competence-label">{{ row.label }}</span>
                <span class="competence-value">{{ row.value }}</span>
            </div>
        </div>

        <div class="stat-grid">
            <template v-if="!isCompetence">
                <div class="stat-tile">
                    <span class="stat-value">{{ summary.exercises }}</span>
                    <span class="stat-label">{{ $tc('exercises.exercises', 2) }}</span>
                </div>
                <div class="stat-tile">
                    <span class="stat-value">{{ summary.sets }}</span>
                    <span class="stat-label">{{ $tc('exercises.sets', 2) }}</span>
                </div>
                <div class="stat-tile">
                    <span class="stat-value">{{ summary.reps }}</span>
                    <span class="stat-label">{{ $t('exercises.reps') }}</span>
                </div>
            </template>

            <div class="stat-tile" :class="{ 'stat-tile--link': preSummary }" @click="showPreSummaryDialog">
                <span class="stat-value">{{ summary.wellness }}<span class="stat-unit">/5</span></span>
                <span class="stat-label">
                    {{ $t('common.wellness') }}
                    <v-icon v-if="preSummary" size="13" class="stat-link-ic">mdi-chevron-right</v-icon>
                </span>
            </div>
            <template v-if="hasPost">
                <div class="stat-tile">
                    <span class="stat-value">{{ summary.duration }}</span>
                    <span class="stat-label">{{ $tc('common.minutes', 2) }}</span>
                </div>
                <div class="stat-tile">
                    <span class="stat-value">{{ summary.effort }}<span class="stat-unit">/10</span></span>
                    <span class="stat-label">{{ $t('exercises.intensity') }}</span>
                </div>
                <div class="stat-tile">
                    <span class="stat-value">{{ summary.charge }}</span>
                    <span class="stat-label">{{ $t('common.charge') }}</span>
                </div>
            </template>
        </div>

        <div v-if="!hasPost" class="summary-pending">
            <v-icon size="16" color="txtMuted">mdi-clock-outline</v-icon>
            <span>{{ $t('routines.post_not_completed') }}</span>
        </div>

        <div v-if="summary.changes.length > 0" class="summary-changes">
            <span class="summary-changes-label">{{ $t('routines.modified_settings') }}</span>
            <change-exercise-card v-for="(change, i) in summary.changes" :change="change" :key="i"
                @click:video="onWatchStudentVideo" />
        </div>

        <div v-if="hasPost && summary.comment" class="summary-comment">
            <span class="summary-comment-label">{{ $t('common.comment') }}</span>
            <span>{{ summary.comment }}</span>
        </div>

        <v-btn v-if="!hideBackRoutines" class="black--text mt-2" block color="primary" depressed large @click="emitClick">
            {{ $t('routines.back_to_routines') }}
        </v-btn>

        <questionnaire-summary :is-third-dialog="isSecondDialog" :is-second-dialog="!isSecondDialog" ref="preSummary" />

        <watch-video-dialog :video="studentVideo" ref="studentVideoDialog" />
    </div>
</template>

<script>
import ChangeExerciseCard from './ChangeExerciseCard.vue'
import WatchVideoDialog from '@/components/common/routines/WatchVideoDialog'
import QuestionnaireSummary from './QuestionnaireSummary.vue';
export default {
    components: { ChangeExerciseCard, QuestionnaireSummary, WatchVideoDialog },
    name: "SummaryData",
    props: {
        summary: {
            type: Object,
            default: null
        },
        preSummary: {
            type: Array,
            default: null
        },
        isCompetence: {
            type: Boolean,
            default: false,
        },
        hideBackRoutines: {
            type: Boolean,
            default: false
        },
        isSecondDialog: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            studentVideo: null,
        }
    },
    computed: {
        hasPost(vm = this) {
            return vm.summary?.hasPost !== false
        },
        competenceRows(vm = this) {
            const rows = []
            if (vm.summary?.versus) rows.push({ label: vm.$t('statistics.rival'), value: vm.summary.versus })
            if (!vm.hasPost) return rows
            if (vm.summary?.team) rows.push({ label: vm.$t('common.team'), value: vm.summary.team })
            if (vm.summary?.position) rows.push({ label: vm.$t('common.position'), value: vm.summary.position })
            return rows
        },
    },
    methods: {
        onWatchStudentVideo(change) {
            const vm = this
            if (!change?.url) return
            vm.studentVideo = change.url
            vm.$nextTick(() => vm.$refs.studentVideoDialog.show(false))
        },
        emitClick() {
            const vm = this
            vm.$emit('back')
        },
        showPreSummaryDialog() {
            const vm = this;
            if (!vm.preSummary) return;
            vm.$refs.preSummary.show(vm.preSummary)
        }
    }
}
</script>

<style scoped>
.summary-data {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.summary-pending {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px dashed var(--v-outline2-base);
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.competence-card {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px;
    border-radius: 12px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
}

.competence-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.competence-label {
    font-size: 11px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.competence-value {
    font-size: var(--fs-body);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    text-align: right;
    min-width: 0;
    overflow-wrap: anywhere;
}

.stat-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.stat-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 16px 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    text-align: center;
}

.stat-tile--link {
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
}

.stat-tile--link:hover {
    border-color: var(--v-secondary-base);
    background: rgba(102, 204, 204, 0.08);
}

.stat-value {
    font-size: 22px;
    font-weight: 800;
    line-height: 1.1;
    color: var(--v-txtPrimary-base);
}

.stat-unit {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.stat-label {
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v-txtSecondary-base);
}

.stat-link-ic {
    color: var(--v-accent-base);
}

.summary-changes {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.summary-changes-label {
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: .1em;
}

.summary-comment {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    line-height: 1.5;
}

.summary-comment-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-txtMuted-base);
}
</style>
